import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { getName, getDescription } from "../../utils";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { Add, Remove, ShoppingCartCheckout } from "@mui/icons-material";
import AddToCart from "../AddToCart";
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from "../../AuthContext";
import SocialShare from "./SocialShare";
import { AddToWishlist } from "../AddToWishlist";
import { useCart } from "react-use-cart";

const Content = ({ product, setProduct, t, wishlistNotify, setWishlistNotify }) => {
  const [quantity, setQuantity] = useState(1);
  const { currency, exchangeRate } = useContext(AuthContext);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const handleBuyNow = (e) => {
    e.stopPropagation();

    if (product.stocks === 0) return;

    const data = {
      id: product.id,
      name: product.name,
      name_ar: product.name_ar || product.name,
      name_ur: product.name_ur || product.name,
      stocks: product.stocks,
      price: product.discount_price ?? product.price,
      tax: product.tax || 0,
      quantity: quantity,
      img: Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : ""
    };

    addItem(data, quantity);
    navigate("/cart");
  };

  return (
    <Box className="h-full w-full px-0 sm:px-10 mt-8 md:pl-10">
      <Box className="flex items-start justify-between">
        <Typography className="poppins text-lg capitalize py-1.5">
          {product.code}
        </Typography>
        <Box className="w-fit bg-[#aaf9d6] px-2 pr-2 poppins text-xs rounded-2xl text-[#0F804D]">
          {product.stocks > 0 ? t("product.stock_availability") : t("product.stock_unavailability")}
        </Box>
        <AddToWishlist
          product={product}
          products={[product]}
          setProducts={(updatedProducts) => {
            if (updatedProducts.length > 0 && setProduct) {
              setProduct(updatedProducts[0]);
            }
          }}
          viaCategory={false}
          open={wishlistNotify}
          setOpen={setWishlistNotify}
        />
      </Box>

     <Typography
        component="h1"
        className="poppins text-4xl font-semibold w-full sm:w-3/4 text-[#2E2E2E] capitalize py-2">
        {getName(product, i18n.language)}
      </Typography>

      <Typography className="poppins text-sm text-[#02ADEC] capitalize py-1.5 font-bold">
        {t("product.by")} {product.brand}
      </Typography>
      <Typography className="poppins text-sm text-[#5D5D5D] capitalize py-1.5">
        <span className="pe-1 font-bold">{t("product.availability")}: </span>
        <span className="bg-[#16A34A] px-3 py-1.5 rounded-md font-bold text-white text-sm">
          {product.stocks} {t("product.available_in_stock")}
        </span>
      </Typography>

      <Box className="flex">
        <Typography className="poppins text-sm text-[#2E2E2E] capitalize py-1.5 pe-2">
          {t("product.tag")}:
        </Typography>
        <Typography className="poppins text-sm text-[#5D5D5D] capitalize py-1.5">
          {product.tags}
        </Typography>
      </Box>

      <Box className="flex items-center gap-x-3 my-3">
        {product.discount_percentage && (
          <Box>
            <Box className="bg-[#FF3030] px-3 py-1.5 rounded-md mb-2">
              <Typography className="poppins text-sm font-bold text-white uppercase">
                {product.discount_percentage}% {t("product.off")}
              </Typography>
            </Box>
            <Typography className="poppins text-base line-through text-[#FF3030] capitalize">
              {Math.round(product.price * exchangeRate * 100) / 100} {currency}
            </Typography>
          </Box>
        )}
        <Typography className="poppins text-4xl font-bold text-[#1E55AC] uppercase py-1.5">
          {currency} {Math.round((product.discount_price ?? product.price) * exchangeRate * 100) / 100}
        </Typography>
      </Box>

      <SocialShare product={product} />

     <Box
  className="poppins text-md text-[#5D5D5D]"
  sx={{
    textAlign: "justify",
    textJustify: "inter-word",
    wordSpacing: "0.05em",
    lineHeight: 1.6,
  }}
  dangerouslySetInnerHTML={{
    __html: getDescription(product, i18n.language),
  }}
/>

      {/* Quantity Row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: 2 }}>
        <Typography
          sx={{
            fontFamily: '"Poppins", sans-serif',
            color: "#2E2E2E",
            textTransform: "capitalize",
            whiteSpace: "nowrap",
            fontSize: { xs: "0.78rem", sm: "0.82rem", md: "0.85rem", lg: "0.9rem" },
          }}
        >
          {t("product.choose_quantity")}:
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #e0e0e0",
            borderRadius: "999px",
            overflow: "hidden",
          }}
        >
          <IconButton
            size="small"
            onClick={() => quantity !== 1 && setQuantity(quantity - 1)}
            sx={{
              background: "#E9E9E9",
              borderRadius: "999px",
              m: "3px",
              p: { xs: "4px", md: "5px", lg: "6px" },
              "&:hover": { background: "#d1d5db" },
            }}
          >
            <Remove sx={{ fontSize: { xs: "0.9rem", md: "1rem", lg: "1.1rem" } }} />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 700,
              color: "#2858A4",
              fontFamily: '"Poppins", sans-serif',
              fontSize: { xs: "0.82rem", md: "0.88rem", lg: "0.95rem" },
              px: { xs: 1.5, md: 2 },
              userSelect: "none",
            }}
          >
            {quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => quantity !== product.stocks && setQuantity(quantity + 1)}
            sx={{
              background: "#E9E9E9",
              borderRadius: "999px",
              m: "3px",
              p: { xs: "4px", md: "5px", lg: "6px" },
              "&:hover": { background: "#d1d5db" },
            }}
          >
            <Add sx={{ fontSize: { xs: "0.9rem", md: "1rem", lg: "1.1rem" } }} />
          </IconButton>
        </Box>
      </Box>

      {/* Buttons Row */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1.5, sm: 1.5 },
          mt: 2,
          alignItems: "stretch",
        }}
      >
        {/* Add to Cart */}
        <Box sx={{ flex: 1 }}>
          <AddToCart
            quantity={quantity}
            product={product}
            className="w-full rounded-3xl text-white poppins font-semibold capitalize transition-colors duration-200 bg-[#1E1E1E] hover:bg-[#2858a3]"
            sx={{
              width: "100%",
              borderRadius: "999px",
              py: { xs: "8px", sm: "7px", md: "8px" },
              fontSize: { xs: "0.78rem", sm: "0.75rem", md: "0.88rem", lg: "1rem" },
              whiteSpace: "nowrap",
            }}
          />
        </Box>

        {/* Buy Now */}
        <Button
          className="poppins"
          variant="contained"
          onClick={handleBuyNow}
          disabled={product.stocks === 0}
          startIcon={<ShoppingCartCheckout sx={{ fontSize: { xs: "1rem", sm: "0.95rem", md: "1.1rem" } }} />}
          sx={{
            flex: 1,
            background: "#2858a3",
            borderRadius: "999px",
            textTransform: "capitalize",
            fontWeight: 600,
            whiteSpace: "nowrap",
            py: { xs: "8px", sm: "7px", md: "8px" },
            fontSize: { xs: "0.78rem", sm: "0.75rem", md: "0.88rem", lg: "1rem" },
            boxShadow: "none",
            "&:hover": { background: "#02AFF3", boxShadow: "none" },
            "&.Mui-disabled": { background: "#e0e0e0" },
            transition: "background 0.2s",
          }}
        >
         Buy Now
        </Button>

        {/* Bulk Inquiry */}
        <Button
          className="poppins"
          variant="contained"
          component={Link}
          to="/contact"
          sx={{
            flex: 1,
            background: "#02AFF3",
            borderRadius: "999px",
            textTransform: "capitalize",
            fontWeight: 600,
            whiteSpace: "nowrap",
            py: { xs: "8px", sm: "7px", md: "8px" },
            fontSize: { xs: "0.78rem", sm: "0.75rem", md: "0.88rem", lg: "1rem" },
            boxShadow: "none",
            "&:hover": { background: "#1E1E1E", boxShadow: "none" },
            transition: "background 0.2s",
          }}
        >
          {t("product.bulk_inquiry")}
        </Button>
      </Box>
    </Box>
  );
};

export default Content;